export interface RmcFix {
  lat: number
  lon: number
  dateTime: Date
}

const RMC_PATTERN = /\$(G[PNL]RMC,[^\r\n*$]*)\*([0-9A-Fa-f]{2})/g

/**
 * Find the first valid RMC sentence ($GPRMC, $GNRMC, $GLRMC) in decoded text.
 * Sentences with a bad checksum or status 'V' (void, no fix) are skipped.
 * Used for dashcams that embed ASCII NMEA in their MP4 files (BlackVue and others).
 */
export function findRmcFix(text: string): RmcFix | null {
  for (const match of text.matchAll(RMC_PATTERN)) {
    if (checksum(match[1]) !== match[2].toUpperCase()) continue
    const fix = parseRmc(match[1])
    if (fix) return fix
  }
  return null
}

function checksum(body: string): string {
  let x = 0
  for (let i = 0; i < body.length; i++) x ^= body.charCodeAt(i)
  return x.toString(16).toUpperCase().padStart(2, '0')
}

function parseRmc(body: string): RmcFix | null {
  const parts = body.split(',')
  // [0]=GPRMC, [1]=time, [2]=status, [3]=lat, [4]=N/S, [5]=lon, [6]=E/W, [9]=date
  if (parts.length < 10) return null
  if (parts[2] !== 'A') return null

  const lat = nmeaToDecimal(parts[3], parts[4])
  const lon = nmeaToDecimal(parts[5], parts[6])
  if (lat === null || lon === null) return null

  const dateTime = parseNmeaDateTime(parts[1], parts[9])
  if (!dateTime) return null

  return { lat, lon, dateTime }
}

function nmeaToDecimal(value: string, direction: string): number | null {
  const raw = parseFloat(value)
  if (isNaN(raw)) return null
  if (direction !== 'N' && direction !== 'S' && direction !== 'E' && direction !== 'W') return null
  const decimal = Math.floor(raw / 100) + (raw % 100) / 60
  return direction === 'S' || direction === 'W' ? -decimal : decimal
}

function parseNmeaDateTime(timeStr: string, dateStr: string): Date | null {
  // timeStr: HHMMSS or HHMMSS.ss, dateStr: DDMMYY
  if (!/^\d{6}/.test(timeStr) || !/^\d{6}$/.test(dateStr)) return null

  const d = new Date(
    Date.UTC(
      2000 + parseInt(dateStr.slice(4, 6), 10),
      parseInt(dateStr.slice(2, 4), 10) - 1,
      parseInt(dateStr.slice(0, 2), 10),
      parseInt(timeStr.slice(0, 2), 10),
      parseInt(timeStr.slice(2, 4), 10),
      parseInt(timeStr.slice(4, 6), 10)
    )
  )
  return isNaN(d.getTime()) ? null : d
}
